import {
  BasicTableCell,
  BasicTableRow,
  Breadcrumbs,
  Button,
  Content,
  Loading,
} from '@orobix/orobix-fe-components'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'

import AnnotationStatusTag from '../../components/AnnotationStatusTag/AnnotationStatusTag'
import ObservationItem from '../../components/ObservationItem/ObservationItem'
import ObservationsBrowser from '../../components/ObservationsBrowser/ObservationsBrowser'
import { ObservationsBrowserHelpers } from '../../components/ObservationsBrowser/types'
import PageHeader from '../../components/PageHeader/PageHeader'
import ProductClassTag from '../../components/ProductClassTag/ProductClassTag'
import TrainingPropertiesList from '../../components/TrainingPropertiesList/TrainingPropertiesList'
import { useDispatch, useStore } from '../../store/store'
import { ProductType } from '../../store/store.types'
import {
  fetchProduct,
  fetchProject,
  fetchTraining,
} from '../../store/storeActions'
import { Observation } from '../../types'
import { vars } from '../../utils/appConfig'
import { formatDate } from '../../utils/date'
import { overrideLabelStudioSettings } from '../../utils/labelStudioHelper'
import { locations } from '../../utils/locations'
import { persist, retrieve } from '../../utils/storage'
import TrainingsAside from '../Trainings/TrainingsAside'
import AnnotationsCount from './AnnotationsCount'
import useStyles from './TrainingRoot.styles'

interface RouteParams {
  productId: string
  projectId: string
  trainingId: string
}

const TrainingRoot = () => {
  const { t } = useTranslation()
  const classes = useStyles()
  const { product, project, training } = useStore()
  const dispatch = useDispatch()
  const { productId, projectId, trainingId } = useParams<RouteParams>()
  useEffect(() => {
    if (!project) {
      fetchProject(dispatch, Number(projectId))
    }
  }, [dispatch, project, projectId])

  useEffect(() => {
    if (!product) {
      fetchProduct(dispatch, Number(productId))
    }
  }, [dispatch, product, productId])

  useEffect(() => {
    if (!training) {
      fetchTraining(dispatch, Number(trainingId))
    }
  }, [dispatch, training, trainingId])

  useEffect(() => {
    if (product && product.productType) {
      overrideLabelStudioSettings(product.productType.name)
    }
  }, [product])

  const getTagDecorator = (productType: ProductType) =>
    ({
      anomaly: ProductClassTag,
      classification: ProductClassTag,
      segmentation: AnnotationStatusTag,
      classification_patch: AnnotationStatusTag,
    }[productType])

  return product && project && training ? (
    <Content
      aside={<TrainingsAside />}
      asideWidth={vars.asideWidth}
      childrenClassName={classes.content}
      breadcrumbs={
        <Breadcrumbs
          elements={[
            <Link to={locations.projects}>{t('Projects')}</Link>,
            <Link to={locations.products(Number(projectId))}>
              {project.name}
            </Link>,
            <Link
              to={locations.trainings(
                Number(projectId),
                Number(productId),
                Number(trainingId)
              )}
            >
              {product.name}
            </Link>,
            training.name,
          ]}
        />
      }
    >
      <div className={classes.training}>
        <div className={classes.info}>
          <PageHeader title={training.name} />
          <TrainingPropertiesList type="complete" />
        </div>

        <div className={classes.sidePanel}>
          <div className={classes.summary}>
            <div className={classes.observationsCount}>
              {t('{{ count }} image', {
                count: training.observationsCount,
              })}
            </div>
            <div className={classes.annotationsPerClass}>
              <AnnotationsCount metrics={training.metrics} />
            </div>
          </div>
          <ObservationsBrowser
            productType={product.type}
            productId={Number(productId)}
            defaultFilters={{ trainingId: Number(trainingId) }}
            mode={retrieve('observations-browser-mode') || 'grid'}
            onModeChange={(mode) => persist('observations-browser-mode', mode)}
            showUploadObservationsButton={false}
            imageData
            viewingMode
            renderGridItem={(
              o: Observation,
              observationsBrowserHelpers: ObservationsBrowserHelpers
            ) => {
              const { openAnnotationModal } = observationsBrowserHelpers

              return (
                <ObservationItem
                  key={o.id}
                  observation={o}
                  onClick={() => {
                    openAnnotationModal()
                  }}
                  tagDecorator={getTagDecorator(product.type)}
                  isActive={false}
                  isDisabled={false}
                />
              )
            }}
            renderTabularItem={(
              o: Observation,
              _observationsBrowserHelpers: ObservationsBrowserHelpers
            ) => {
              const TagDecorator = getTagDecorator(product.type)
              const { openAnnotationModal } = _observationsBrowserHelpers
              return (
                <BasicTableRow
                  key={o.id}
                  rowData={o}
                  selected={false}
                  muted={false}
                  compact={false}
                >
                  <BasicTableCell th>#{o.id}</BasicTableCell>
                  <BasicTableCell>{o.filename}</BasicTableCell>
                  <BasicTableCell>{formatDate(o.creationDate)}</BasicTableCell>
                  <BasicTableCell>
                    <TagDecorator observation={o} />
                  </BasicTableCell>
                  <BasicTableCell>
                    <div className={classes.tableActionsCell}>
                      <Button
                        label={t('Show annotation')}
                        onClick={(e) => {
                          e.stopPropagation()
                          openAnnotationModal()
                        }}
                        buttonType="button"
                        size="sm"
                      />
                    </div>
                  </BasicTableCell>
                </BasicTableRow>
              )
            }}
          />
        </div>
      </div>
    </Content>
  ) : (
    <Content>
      <Loading />
    </Content>
  )
}

export default TrainingRoot
